// UltraFlex — Top-level Site Selector (header dropdown).
// Uses `useSite()` to read the user's allowed sites and active site, and
// persists the choice via the context. Shows nothing if the user only has
// one site (no point choosing).
import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Check, ChevronDown, Globe } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

export default function SiteSelector({ compact = false, variant = 'desktop' }) {
  const { allowedSites, hasAllSitesAccess, activeSiteId, selectSite, loading } = useSite();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Build per-variant testids — desktop also gets the bare 'site-selector-*'
  // testids so existing tests + Logan's Excel test plan keep working.
  const triggerTid = variant === 'desktop' ? 'site-selector-trigger' : 'site-selector-trigger-mobile';
  const menuTid = variant === 'desktop' ? 'site-selector-menu' : 'site-selector-menu-mobile';
  const singleTid = variant === 'desktop' ? 'site-selector-single' : 'site-selector-single-mobile';

  // Close on outside-click
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (loading) {
    return <div className="text-xs text-uf-text-muted px-3 py-1.5">Loading sites…</div>;
  }

  // Single-site user: just show the label, no dropdown
  if (!hasAllSitesAccess && allowedSites.length <= 1) {
    const site = allowedSites[0];
    if (!site) return null;
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-uf-surface-hi text-uf-text text-xs"
        data-testid={singleTid}
      >
        <MapPin className="w-3.5 h-3.5 text-uf-red" />
        <span className="font-medium">{site.name}</span>
      </div>
    );
  }

  const active = activeSiteId ? allowedSites.find(s => s.id === activeSiteId) : null;
  const label = active ? active.name : 'All Sites';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid={triggerTid}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-uf-surface-hi hover:bg-uf-surface-hi/80 border border-uf-border text-uf-text transition-colors ${compact ? 'text-xs' : 'text-sm'}`}
      >
        {active ? <MapPin className="w-4 h-4 text-uf-red" /> : <Globe className="w-4 h-4 text-uf-red" />}
        <span className="font-medium truncate max-w-[160px]">{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-uf-surface border border-uf-border rounded-lg shadow-xl z-[100] py-1" data-testid={menuTid}>
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-uf-text-muted border-b border-uf-border/50">Active site</div>
          {hasAllSitesAccess && (
            <button
              onClick={() => { selectSite(null); setOpen(false); }}
              data-testid="site-option-all"
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-uf-surface-hi ${!activeSiteId ? 'text-uf-red font-semibold' : 'text-uf-text'}`}
            >
              <span className="flex items-center gap-2"><Globe className="w-4 h-4" /> All Sites</span>
              {!activeSiteId && <Check className="w-4 h-4" />}
            </button>
          )}
          <div className="max-h-72 overflow-y-auto">
            {allowedSites.map(site => (
              <button
                key={site.id}
                onClick={() => { selectSite(site.id); setOpen(false); }}
                data-testid={`site-option-${site.id}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-uf-surface-hi ${activeSiteId === site.id ? 'text-uf-red font-semibold' : 'text-uf-text'}`}
              >
                <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {site.name}</span>
                {activeSiteId === site.id && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
          {!allowedSites.length && !hasAllSitesAccess && (
            <div className="px-3 py-4 text-xs text-uf-text-muted">No sites assigned to your account.</div>
          )}
        </div>
      )}
    </div>
  );
}
